import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import SuccessPage from "@/pages/SuccessPage";
import FailedPage from "@/pages/FailedPage";
import { Loader2Icon } from "lucide-react";
import { getAppIdFromUrl, storeAppIdFallback, getDecodedTokenFromSource } from "@/lib/token-utils";

const Return = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Wrap everything in try-catch to prevent JWT errors from blocking redirects
    try {
      sessionStorage.removeItem('paypal_redirect_in_progress');
      
      const success = searchParams.get("success");
      const error = searchParams.get("error");
      const paypalToken = searchParams.get("token");
      const payerId = searchParams.get("PayerID");
      const paypalStatus = searchParams.get("paypal_status");
      const sessionId = searchParams.get('pi');
      const jwtFromUrl = searchParams.get('jwt');

    const appIdFromUrl = getAppIdFromUrl();
    if (appIdFromUrl) {
      storeAppIdFallback(appIdFromUrl);
    }

    // CRITICAL: If JWT is in URL (from PayPal return), store it BEFORE making any API calls
    // This ensures authentication works when user returns from PayPal with fresh token
    if (jwtFromUrl && jwtFromUrl.startsWith('eyJ')) {
      sessionStorage.setItem('token', jwtFromUrl);
    }

    if (paypalStatus === 'cancelled' || paypalStatus === 'vaulted_cancelled') {
      if (paypalStatus === 'vaulted_cancelled' && sessionId) {
        const handleWrappedCancellation = async () => {
          try {
          // Get native return URL from JWT token instead of session metadata
          const decodedToken = getDecodedTokenFromSource('storage');
          const nativeReturnUrl = decodedToken?.nativeReturnUrl;
          
          if (nativeReturnUrl) {
            // Store the native return URL in sessionStorage so FailedPage can use it
            sessionStorage.setItem('native_return_url', nativeReturnUrl);
            setStatus("canceled");
            setLoading(false);
            return;
          }
          } catch (error) {
            // Silent handling
          }
          
          setStatus("canceled");
          setLoading(false);
        };
        
        handleWrappedCancellation();
        return;
      }
      
      setStatus("canceled");
      setLoading(false);
      return;
    }

    // Handle wrapped PayPal vaulted flow (native apps) - only if NO paypalToken/payerId in URL
    // Regular vaulted flow has token/PayerID, wrapped flow doesn't
    if (paypalStatus === 'vaulted_success' && sessionId && !paypalToken && !payerId) {
      const captureKey = `wrapped_paypal_capture_${sessionId}`;
      const captureStatus = sessionStorage.getItem(captureKey);
      if (captureStatus === 'processing' || captureStatus === 'completed') {
        if (captureStatus === 'completed') {
          setStatus("succeeded");
          setLoading(false);
        }
        return;
      }
      
      const handleWrappedReturn = async () => {
        try {
          sessionStorage.setItem(captureKey, 'processing');
          
          // Get native return URL from JWT token instead of session metadata
          const decodedToken = getDecodedTokenFromSource('storage');
          const nativeReturnUrl = decodedToken?.nativeReturnUrl;
          
          if (nativeReturnUrl) {
            // Store the native return URL in sessionStorage so SuccessPage can use it
            sessionStorage.setItem('native_return_url', nativeReturnUrl);
            setStatus("succeeded");
            setLoading(false);
            return;
          }
          
          const { processPayment } = await import('@/services/PaymentService');
          
          const chargePayload = {
            session_id: sessionId,
            save_payment_method: false,
            is_new_payment_method: true,
            payment_method: {
              data: {
                provider: {
                  provider: 'custom',
                  config: {
                    'paypal-type': 'vaulted'
                  }
                },
                paypal_token: paypalToken,
                payer_id: payerId,
                description: 'Game Purchase'
              }
            }
          };
          
          const captureResult = await processPayment(chargePayload);
          
          if (captureResult.result.is_success) {
            sessionStorage.setItem(captureKey, 'completed');
            setStatus("succeeded");
            setLoading(false);
          } else {
            sessionStorage.removeItem(captureKey);
            setStatus("error");
            setLoading(false);
          }
        } catch (error) {
          sessionStorage.removeItem(captureKey);
          setStatus("error");
          setLoading(false);
        }
      };
      
      handleWrappedReturn();
      return;
    }

    // Handle PayPal re-authentication success - redirect back to checkout to retry payment
    if (paypalStatus === 'reauth_success' && sessionId) {
      // Try to restore the JWT token from sessionStorage backup
      const storedJwtToken = sessionStorage.getItem('original_jwt_token') || 
                            sessionStorage.getItem('token');
      
      if (storedJwtToken && storedJwtToken.startsWith('eyJ')) {
        sessionStorage.setItem('token', storedJwtToken);
      }
      
      // Redirect back to checkout to retry the payment with new token
      const currentUrl = new URL(window.location.href);
      const checkoutUrl = new URL(`${currentUrl.origin}/checkout`);
      
      // Copy relevant parameters
      searchParams.forEach((value, key) => {
        if (key !== 'paypal_status' && key !== 'payment_method') {
          checkoutUrl.searchParams.set(key, value);
        }
      });
      
      // Add a flag to indicate this is after re-authentication
      checkoutUrl.searchParams.set('paypal_reauth_complete', 'true');
      
      window.location.href = checkoutUrl.toString();
      return;
    }

    // Handle PayPal success return (both vaulted and non-vaulted) - redirect back to checkout to complete payment
    if ((paypalStatus === 'success' || paypalStatus === 'vaulted_success') && paypalToken && payerId && sessionId) {
      
      // Try to restore the JWT token from sessionStorage backup
      const storedJwtToken = sessionStorage.getItem('original_jwt_token') || 
                            sessionStorage.getItem('token');
      
      if (storedJwtToken && storedJwtToken.startsWith('eyJ')) {
        sessionStorage.setItem('token', storedJwtToken);
      }
      
      // Include all existing query parameters and add PayPal parameters
      const currentUrl = new URL(window.location.href);
      const checkoutUrl = new URL(`${currentUrl.origin}/checkout`);
      
      // Copy all current search parameters except 'token' and 'paypal_status' to avoid confusion
      searchParams.forEach((value, key) => {
        if (key !== 'token' && key !== 'paypal_status') {
          checkoutUrl.searchParams.set(key, value);
        }
      });
      
      // Add PayPal-specific parameters with renamed token to avoid JWT confusion
      checkoutUrl.searchParams.set('paypal_token', paypalToken);
      checkoutUrl.searchParams.set('PayerID', payerId);
      
      // Include JWT if available (either from URL or storage)
      const jwtForCheckout = jwtFromUrl || storedJwtToken;
      if (jwtForCheckout && jwtForCheckout.startsWith('eyJ')) {
        checkoutUrl.searchParams.set('jwt', jwtForCheckout);
      }
      
      
      // Small delay to ensure logs are visible
      setTimeout(() => {
        window.location.href = checkoutUrl.toString();
      }, 100);
      return;
    }

    // Handle missing session ID in PayPal flow
    if ((paypalStatus === 'success' || paypalStatus === 'vaulted_success') && (!sessionId || !paypalToken || !payerId)) {
      console.error('[PayPal Return] Missing required parameters:', { sessionId, paypalToken: paypalToken ? 'present' : 'missing', payerId: payerId ? 'present' : 'missing' });
      setStatus("error");
      setLoading(false);
      return;
    }

    // Handle regular success/error states (non-PayPal)
    if (success === "true") {
      // Check if this is from a wrapped flow with native return URL
      const checkForNativeReturnUrl = async () => {
        if (sessionId) {
          try {
            // Get native return URL from JWT token instead of session metadata
            const decodedToken = getDecodedTokenFromSource('storage');
            const nativeReturnUrl = decodedToken?.nativeReturnUrl;
            
            if (nativeReturnUrl) {
              sessionStorage.setItem('native_return_url', nativeReturnUrl);
            }
          } catch (error) {
            // Silently handle JWT decoding errors
          }
        }
        
        setStatus("succeeded");
        setLoading(false);
      };
      
      checkForNativeReturnUrl();
      return;
    }

    if (error) {
      setStatus("error");
      setLoading(false);
      return;
    }

    // Handle legacy PayPal returns (without paypal_status parameter)
    // This is for backward compatibility with existing PayPal integrations
    if (paypalToken && payerId && sessionId) {
      // Treat as success and redirect to checkout
      const storedJwtToken = sessionStorage.getItem('original_jwt_token') || 
                            sessionStorage.getItem('token');
      
      if (storedJwtToken && storedJwtToken.startsWith('eyJ')) {
        sessionStorage.setItem('token', storedJwtToken);
      }
      
      const currentUrl = new URL(window.location.href);
      const checkoutUrl = new URL(`${currentUrl.origin}/checkout`);
      
      searchParams.forEach((value, key) => {
        if (key !== 'token') {
          checkoutUrl.searchParams.set(key, value);
        }
      });
      
      checkoutUrl.searchParams.set('paypal_token', paypalToken);
      checkoutUrl.searchParams.set('PayerID', payerId);
      
      window.location.href = checkoutUrl.toString();
      return;
    }

    // If we reach here and there's no clear status, it might be an error or incomplete state
    if (sessionId && !success && !error && !paypalStatus) {
      setStatus("error");
      setLoading(false);
      return;
    }

    setLoading(false);
    } catch (err) {
      
      setLoading(false);
    }
  }, [searchParams]);

  if (loading)
    return (
      <div className="flex justify-center items-center h-[85vh]">
        <Loader2Icon className="animate-spin" />
        <p className="ml-2">
          Securely processing your payment. Please don't close this window.
        </p>
      </div>
    );

  switch (status) {
    case "complete":
    case "succeeded":
    case "COMPLETED":
      return <SuccessPage />;
    case "requires_payment_method":
    case "open":
    case "incomplete":
    case "canceled":
    case "error":
    case "no-id":
    default:
      return <FailedPage />;
  }
};

export default Return;
